<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-form
          ref="form"
          v-model="isFormValid"
        >
          <v-container fluid>
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="edited.legal_case_type_id"
                  outlined
                  :hint="legalCaseTypeDescription"
                  :items="selectedLegalCaseTypesOptions"
                  :rules="[rules.required]"
                  label="Case type *"
                  persistent-hint
                >
                  <template #selection="data">
                    <span>
                      {{ data.item.name }}
                      <v-icon
                        v-if="data.item.has_rules"
                        color="black"
                        right
                        size="18"
                      >
                        toc
                      </v-icon>
                    </span>
                  </template>

                  <template #item="data">
                    <template v-if="!isObject(data.item)">
                      <!-- eslint-disable vue/no-v-text-v-html-on-component -->
                      <v-list-item-content v-text="data.item" />
                      <!-- eslint-enable -->
                    </template>

                    <template v-else>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                          <v-icon
                            v-if="data.item.has_rules"
                            color="black"
                            right
                            size="18"
                          >
                            toc
                          </v-icon>
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col class="xs12">
                <UserSelect
                  v-model="edited.primary_attorney_id"
                  :rules="[rules.required]"
                  outlined
                  hide-disabled
                  :hint="isCreate ? '' : 'Changes to Primary Attorney will reassign incomplete Tasks & Events'"
                  label="Primary Attorney *"
                  only-attorneys
                  persistent-hint
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <UserSelect
                  v-model="edited.primary_legal_assistant_id"
                  :rules="[rules.primary_legal_assistant_id]"
                  clearable
                  hide-disabled
                  :hint="isCreate ? '' : 'Changes to Primary Legal Assistant will reassign incomplete Tasks & Events'"
                  label="Primary Legal Assistant"
                  outlined
                  persistent-hint
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <UserSelect
                  v-model="edited.other_attorney_ids"
                  :rules="[rules.other_attorney_ids]"
                  clearable
                  :exclude-users="[edited.primary_attorney_id]"
                  hide-disabled
                  hide-selected
                  label="Other Attorneys"
                  multiple
                  only-attorneys
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <UserSelect
                  v-model="edited.other_legal_assistant_ids"
                  :rules="[rules.other_legal_assistant_ids]"
                  clearable
                  :exclude-users="[edited.primary_legal_assistant_id]"
                  hide-disabled
                  hide-selected
                  label="Other Legal Assistants"
                  multiple
                  outlined
                />
              </v-col>

              <template v-if="instanceHasPreferenceCategories">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-autocomplete
                    v-model="edited.preference_category_id"
                    clearable
                    :items="preferenceCategoriesOptions"
                    label="Preference Category"
                    outlined
                  />
                </v-col>
              </template>

              <BasicDateField
                :date="edited.opened"
                :max-date="edited.closed"
                label="Open Date *"
                outlined
                required
                @update-date="newDate => updateOpenDateField(newDate)"
                @valid-changed="newVal => updateDatesValid(newVal)"
              />

              <v-col cols="12">
                <v-row
                  align="center"
                  no-gutters
                >
                  <v-divider />
                  <span class="mx-4 text-caption text-uppercase">
                    Optional information
                  </span>
                  <v-divider />
                </v-row>
              </v-col>

              <v-col
                v-if="isCreate"
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="edited.tags"
                  chips
                  counter="5"
                  deletable-chips
                  hide-selected
                  outlined
                  multiple
                  small-chips
                  :items="availableOptionsTags"
                  label="Tags"
                  :rules="[rules.tagsCount]"
                >
                  <template #selection="{ item }">
                    <v-chip
                      close
                      label
                      :color="item.color"
                      @click:close="removeTagSelection(item.value)"
                    >
                      {{ item.text }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>

              <BasicDateField
                :date="edited.priority_date"
                label="Priority Date"
                outlined
                @update-date="newDate => updatePriorityDateField(newDate)"
                @valid-changed="newVal => updateDatesValid(newVal)"
              />

              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="edited.chargeability_id"
                  clearable
                  :items="countriesOptions"
                  label="Chargeability"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="edited.storage"
                  label="Storage"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="edited.label"
                  label="Label"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="edited.volume"
                  label="Volume"
                  outlined
                />
              </v-col>

              <v-col
                v-if="instanceHasLocations"
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="edited.location_id"
                  clearable
                  :items="locationsOptions"
                  label="Location"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <TmetricFields
                  v-if="syncableWithTmetric"
                  @update="data => updateTmetricAttributes(data)"
                />

                <p
                  v-if="edited.tmetric_synced_at"
                  class="mt-5"
                >
                  This case has been pushed to Tmetric on {{ edited.tmetric_synced_at | formatDateTime }}.
                </p>
              </v-col>

              <v-row v-if="!edited.closed && !isCreate">
                <v-col>
                  <CaseClosingWizard
                    v-model="closingWizardOpened"
                    :legal-case="edited"
                    @case-closed="docketEntrydId => $emit('case-closed', docketEntrydId)"
                  />
                </v-col>
              </v-row>
            </v-row>
          </v-container>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { isEmpty, isObject } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import Vue from 'vue'

import { dateDisplayFormat, dateISOFormat } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { CHECKLIST_NAMESPACE } from '@/store/checklist'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { LEGAL_CASE_TYPE_NAMESPACE } from '@/store/legal-case-type'
import { RECORD_NAMESPACE } from '@/store/record'
import { SHARED_NAMESPACE } from '@/store/shared'

import BasicDateField from '@/components/Fields/BasicDate.vue'
import CaseClosingWizard from '@/components/Dialogs/Case/CaseClosingWizard.vue'
import TmetricFields from '@/components/Dialogs/Case/TmetricFields.vue'
import UserSelect from '@/components/Fields/UserSelect.vue'

export default {
  name: 'LegalCaseEditorOverview',

  components: {
    BasicDateField,
    CaseClosingWizard,
    TmetricFields,
    UserSelect,
  },

  props: {
    isCreate: {
      type: Boolean,
      required: true,
    },

    valid: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      closingWizardOpened: false,
      dependentNamedAssignees: [],
      rules: {
        ...rules,
        other_attorney_ids: _ => true,
        other_legal_assistant_ids: _ => true,
        primary_legal_assistant_id: _ => true,
        tagsCount: items => items.length <= 5,
      },
    }
  },

  computed: {
    availableOptionsTags () {
      const tags = this.edited?.tags?.map(item => item.id)
      const availableTags = this.optionsTags?.filter(item => !tags?.includes(item.value))

      return availableTags
    },

    instanceHasLocations () {
      return this.locationsOptions.length > 0
    },

    instanceHasPreferenceCategories () {
      return this.preferenceCategoriesOptions.length > 0
    },

    isFormValid: {
      get () { return this.valid && this.validDates },
      set (value) { this.$emit('update:valid', value) },
    },

    legalCaseType () {
      if (!this.edited) return

      return this.legalCaseTypesOptions?.find(l => l.value === this.edited.legal_case_type_id)
    },

    legalCaseTypeDescription () {
      return this.legalCaseType?.description
    },

    optionsTags () { return this.getCachedDropdown('tags') },

    syncableWithTmetric () {
      return this.isTmetricEnabledForCurrentUser && [null, undefined].includes(this.edited.tmetric_synced_at)
    },

    ...mapGetters([
      'countriesOptions',
      'legalCaseTypesOptions',
      'locationsOptions',
      'preferenceCategoriesOptions',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'edited',
    ]),

    ...mapGetters(LEGAL_CASE_TYPE_NAMESPACE, [
      'selectedLegalCaseTypesOptions',
    ]),

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
      'getWorkflowSharedList',
      'isTmetricEnabledForCurrentUser',
    ]),
  },

  watch: {
    'edited.legal_case_type_id': {
      immediate: true,

      handler (val) {
        if ([null, undefined].includes(val)) this.clearWorkflowSharedDetails()
        else {
          this.
            fetchWorkflowSharedDetails({ id: val, model: 'legal_case_types' }).
            then(_ => {
              this.dependentNamedAssignees = this.getWorkflowSharedList.map(workflow => workflow.assignees).flat()
              this.setValidationRules()
              this.$nextTick().then(this.validate)
            })
        }

        const checklistId = this.legalCaseType?.checklist_id
        this.isChecklistEditable = !(this.edited.checklist || (checklistId && this.isCreate))

        if (checklistId && this.isCreate) {
          this.getChecklist(checklistId).
            then(({ checklist }) => {
              Vue.set(this.edited, 'checklist', checklist)
            })
        }
      },
    },
  },

  async created () {
    this.getCountriesModel()
    this.getFeatures()
    this.getLocationsModel()
    this.getPreferenceCategoriesModel()
    this.getTmetricEnabledForCurrentUser()
    this.getSelectedLegalCaseTypes({
      filters: [
        { filter_name: 'team_ids', filter_value: this.currentRecord?.teams?.map(t => t.id) },
        { filter_name: 'archived', filter_value: false },
      ],
    })
    this.setValidationRules()

    this.getDropdownOptions({ force: true, options: false, path: 'tags' })

    if (!this.edited.opened) this.edited.opened = dateISOFormat()

    this.loadOtherLegalUsers()
    await this.getLegalCaseTypesModel()
  },

  methods: {
    formatDate (unformattedDate) {
      return !isEmpty(unformattedDate) ? dateDisplayFormat(unformattedDate) : ''
    },

    isObject,

    loadOtherLegalUsers () {
      this.edited.other_attorney_ids = this.edited.other_attorneys
        ? this.edited.other_attorneys.map(user => user.id)
        : []
      this.edited.other_legal_assistant_ids = this.edited.other_legal_assistants
        ? this.edited.other_legal_assistants.map(user => user.id)
        : []
    },

    removeTagSelection (value) {
      this.edited.tags = this.edited.tags.filter(el => el !== value)
    },

    setValidationRules () {
      this.rules.other_attorney_ids = _ => true
      this.rules.other_legal_assistant_ids = _ => true
      this.rules.primary_legal_assistant_id = _ => true

      if (this.dependentNamedAssignees.length === 0) return

      if (this.dependentNamedAssignees.includes('other_attorneys')) {
        this.rules.other_attorney_ids = rules.required
      }

      if (this.dependentNamedAssignees.includes('other_legal_assistants')) {
        this.rules.other_legal_assistant_ids = rules.required
      }

      if (this.dependentNamedAssignees.includes('primary_legal_assistant')) {
        this.rules.primary_legal_assistant_id = rules.required
      }
    },

    updateBirthdateBoxField (newVal) {
      this.edited.party.uncertain_dob = newVal
    },

    updateBirthdateField (newDate) {
      this.edited.party.dob = newDate
    },

    updateClosedDateField (newDate) {
      this.edited.closed = newDate
    },

    updateDatesValid (newVal) {
      // this method is called by all date fields - we don't need to know which of the dates is invalid,
      // just that all are valid
      this.validDates = newVal
    },

    updateOpenDateField (newDate) {
      Vue.set(this.edited, 'opened', newDate)
    },

    updatePriorityDateField (newDate) {
      this.edited.priority_date = newDate
    },

    updateTmetricAttributes (data) {
      this.edited.budget_size = data.budgetSize
      this.edited.tmetric_client_id = data.tmetricClientId
    },

    validate () {
      this.$refs.form.validate()
    },

    ...mapActions([
      'getCountriesModel',
      'getLegalCaseTypesModel',
      'getLocationsModel',
      'getPreferenceCategoriesModel',
    ]),

    ...mapActions(CHECKLIST_NAMESPACE, [
      'getChecklist',
    ]),

    ...mapActions(LEGAL_CASE_TYPE_NAMESPACE, [
      'getSelectedLegalCaseTypes',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'clearWorkflowSharedDetails',
      'fetchWorkflowSharedDetails',
      'getDropdownOptions',
      'getFeatures',
      'getTmetricEnabledForCurrentUser',
    ]),
  },
}
</script>
